import React from 'react';
import SlidePreview from './SlidePreview';
import VariableAutocomplete from './VariableAutocomplete';
import {
  GRADIENT_PRESETS,
  type StorySlide,
  type StoryVariableDef,
  type UpdateSlidePayload,
} from '../../../services/storiesAdminService';

interface StorySlideEditorProps {
  slide: StorySlide;
  totalSlides: number;
  slideIndex: number;
  onChange: (patch: UpdateSlidePayload) => void;
  onSave: () => Promise<void> | void;
  onDelete: () => void;
  saving: boolean;
  dirty: boolean;
  /** Доступные переменные для autocomplete (пустой = autocomplete off). */
  availableVars?: StoryVariableDef[];
}

/**
 * Редактор одного слайда: форма слева, превью справа (sticky).
 * Изменения применяются локально, сохраняются по кнопке "Сохранить слайд".
 */
const StorySlideEditor: React.FC<StorySlideEditorProps> = ({
  slide,
  totalSlides,
  slideIndex,
  onChange,
  onSave,
  onDelete,
  saving,
  dirty,
  availableVars,
}) => {
  const vars = availableVars ?? [];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Слайд #{slideIndex + 1}
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
            >
              Удалить слайд
            </button>
            <button
              type="button"
              onClick={() => onSave()}
              disabled={saving || !dirty}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : dirty ? 'Сохранить слайд' : 'Сохранено'}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Eyebrow (подпись над заголовком)
            {vars.length > 0 && <span className="ml-2 text-xs text-gray-400">— наберите <code>{`{{`}</code> для переменных</span>}
          </label>
          <VariableAutocomplete
            value={slide.eyebrow ?? ''}
            onChange={(v) => onChange({ eyebrow: v || null })}
            variables={vars}
            placeholder="Напр. «Новое»"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Заголовок *
            {vars.length > 0 && <span className="ml-2 text-xs text-gray-400">— <code>{`{{readiness.score}}`}</code> и т.п.</span>}
          </label>
          <VariableAutocomplete
            value={slide.headline}
            onChange={(v) => onChange({ headline: v })}
            variables={vars}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Шрифт заголовка
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange({ headline_font: 'sans' })}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                slide.headline_font === 'sans'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Sans (Inter)
            </button>
            <button
              type="button"
              onClick={() => onChange({ headline_font: 'serif' })}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                slide.headline_font === 'serif'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Serif (Georgia)
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Текст
          </label>
          <VariableAutocomplete
            value={slide.body ?? ''}
            onChange={(v) => onChange({ body: v || null })}
            variables={vars}
            multiline
            rows={3}
            placeholder="Описание (опционально)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Градиент фона
          </label>
          <div className="flex gap-2 flex-wrap">
            {GRADIENT_PRESETS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => onChange({ background_gradient: g.value })}
                className={`relative h-10 w-16 rounded-lg border-2 transition-transform ${
                  slide.background_gradient === g.value
                    ? 'border-blue-500 scale-105'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                style={{ background: g.value }}
                title={g.label}
              />
            ))}
          </div>
          <input
            type="text"
            value={slide.background_gradient}
            onChange={(e) => onChange({ background_gradient: e.target.value })}
            placeholder="linear-gradient(...) — кастомный CSS"
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Цвет текста
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={slide.text_color || '#FFFFFF'}
                onChange={(e) => onChange({ text_color: e.target.value })}
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={slide.text_color}
                onChange={(e) => onChange({ text_color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Длительность (мс)
            </label>
            <input
              type="number"
              min={1000}
              max={30000}
              step={500}
              value={slide.duration_ms || 5000}
              onChange={(e) =>
                onChange({ duration_ms: Math.max(1000, Math.min(30000, parseInt(e.target.value, 10) || 5000)) })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {((slide.duration_ms || 5000) / 1000).toFixed(1)}s
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL изображения (опционально)
          </label>
          <input
            type="url"
            value={slide.image_url ?? ''}
            onChange={(e) => onChange({ image_url: e.target.value || null })}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CTA — текст кнопки
            </label>
            <VariableAutocomplete
              value={slide.cta_label ?? ''}
              onChange={(v) => onChange({ cta_label: v || null })}
              variables={vars}
              placeholder="Попробовать"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CTA — URL
            </label>
            <VariableAutocomplete
              value={slide.cta_url ?? ''}
              onChange={(v) => onChange({ cta_url: v || null })}
              variables={vars}
              placeholder="/dashboard"
            />
          </div>
        </div>

        {/* Iterate-over для system stories (например 1 слайд на каждое событие) */}
        <div className="border-t border-gray-200 pt-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Iterate-over (опционально)
              </label>
              <select
                value={slide.iterate_over ?? ''}
                onChange={(e) => onChange({
                  iterate_over: e.target.value || null,
                  set_iterate_over_null: !e.target.value,
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— не итерировать —</option>
                <option value="events.upcoming">events.upcoming (1 слайд / событие)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Если задано — resolver склонирует слайд N раз. Внутри доступны <code>{`{{item.X}}`}</code> переменные.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max iterations (cap)
              </label>
              <input
                type="number"
                min={1}
                max={20}
                step={1}
                value={slide.max_iterations ?? 5}
                onChange={(e) =>
                  onChange({ max_iterations: Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 5)) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                1..20. Default 5. Применяется только при iterate-over.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start">
        <p className="text-xs text-gray-500 mb-2 text-center">
          Предпросмотр (~280×560)
        </p>
        <SlidePreview
          backgroundGradient={slide.background_gradient}
          textColor={slide.text_color}
          eyebrow={slide.eyebrow}
          headline={slide.headline}
          body={slide.body}
          imageUrl={slide.image_url}
          headlineFont={slide.headline_font}
          ctaLabel={slide.cta_label}
          ctaUrl={slide.cta_url}
          totalSlides={totalSlides}
          activeSlideIndex={slideIndex}
        />
      </div>
    </div>
  );
};

export default StorySlideEditor;
